import {useState} from 'react';

import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';

import {useChangeAddressMutation} from '../profile/queries/useChangeAddressMutation';
import {useChangeDataAlertStore} from '../profile/store/changeDataAlertStore';
import {useChangeDataFormStateStore} from '../profile/store/changeDataFormStateStore';
import {MessageEnum, AlertEnum} from '../profile/store/changeDataAlertStore/types';

import {useModalDiscardChangesState} from '../profile/store/modalDiscardChangesStateStore';
import {useFormAccordionStore} from '../profile/store/formAccordionStore';
import {AccordionStateEnum, AccordionStatesType} from '../profile/store/formAccordionStore/types';
import {useModalOtpConfirmationStateStore} from '../profile/store/modalOtpConfirmationStateStore';
import {useConfirmPhoneMutation} from '../profile/queries/useConfirmPhoneMutation';
import {useResendPhoneOtpMutation} from '../profile/queries/useResendPhoneOtpMutation';
import {useConfirmEmailMutation} from '../profile/queries/useConfirmEmailMutation';
import {useResendEmailOtpMutation} from '../profile/queries/useResendEmailOtpMutation';

export function useChangeDataProfile() {
	const setChangeDataAlertState = useChangeDataAlertStore(state => state.setStatus);
	const setChangeDataFormState = useChangeDataFormStateStore(state => state.updateFormState);
	const changeDataFormState = useChangeDataFormStateStore(state => state.formState);
	const resetChangeDataFormState = useChangeDataFormStateStore(state => state.reset);

	const [changingEmailState, setChangingEmailState] = useState<OperationStateType>(
		OperationStateTypesEnum.NONE,
	);
	const [changingCellphoneState, setChangingCellphoneState] = useState<OperationStateType>(
		OperationStateTypesEnum.NONE,
	);

	const nextAccordionState = useFormAccordionStore(state => state.type);
	const setNextAccordionState = useFormAccordionStore(state => state.setType);
	const {mutate: handleChangeAddress, isPending: changeAddressIsLoading} =
		useChangeAddressMutation();
	const setIsModalDiscardChangesOpenState = useModalDiscardChangesState(state => state.setState);
	const isModalDiscardChangesOpenState = useModalDiscardChangesState(state => state.state);
	const isModalOtpConfirmationOpen = useModalOtpConfirmationStateStore(state => state.state);
	const setIsModalOtpConfirmationOpen = useModalOtpConfirmationStateStore(
		state => state.updateState,
	);
	const resetOtpConfirmationModal = useModalOtpConfirmationStateStore(state => state.reset);
	const {mutateAsync: handleSendPhoneOtpCode} = useConfirmPhoneMutation({
		setChangingCellphoneState,
	});
	const {mutateAsync: resendPhoneOtpCode} = useResendPhoneOtpMutation({
		setChangingCellphoneState,
	});
	const {mutateAsync: handleSendEmailOtpCode} = useConfirmEmailMutation({setChangingEmailState});
	const {mutateAsync: resendEmailOtpCode} = useResendEmailOtpMutation({setChangingEmailState});

	const resetCellphoneErrorBoundary = () => {
		setChangingCellphoneState(OperationStateTypesEnum.UNAVAILABLE);
		setIsModalOtpConfirmationOpen({isAbandoned: false});
	};
	const resetEmailErrorBoundary = () => {
		setChangingEmailState(OperationStateTypesEnum.DONE);
		setIsModalOtpConfirmationOpen({isAbandoned: false});
	};

	const handleChangeDataFocus = (name: AccordionStatesType, activeForm?: boolean) => {
		if (changeDataFormState.isFormDirty) {
			setNextAccordionState(name);
			setIsModalDiscardChangesOpenState(true);
			return;
		}

		if (name === changeDataFormState.name && !activeForm) {
			resetChangeDataFormState();
			return;
		}

		setChangeDataFormState({
			name,
			isFormDirty: false,
			isFormActive: activeForm || false,
		});

		setChangeDataAlertState({
			type: AlertEnum.NONE,
			typeMessage: MessageEnum.SUCCESS,
			status: false,
		});
	};

	const handleChangeDataFormOpen = () => {
		setChangeDataFormState({
			isFormDirty: false,
			isFormActive: !changeDataFormState.isFormActive,
		});
	};

	const handleOnConfirmDiscardChangesModal = () => {
		const shouldOpenWithFormActive = [AccordionStateEnum.CELLPHONE, AccordionStateEnum.EMAIL];
		setChangeDataFormState({
			name: nextAccordionState,
			isFormDirty: false,
			isFormActive: shouldOpenWithFormActive.includes(
				nextAccordionState as AccordionStateEnum,
			),
		});

		resetOtpConfirmationModal();

		setIsModalDiscardChangesOpenState(false);
	};

	const onCancel = (name?: AccordionStatesType) => {
		setChangeDataFormState({
			name: name || undefined,
			isFormActive: false,
			isFormDirty: false,
		});
		resetOtpConfirmationModal();
	};

	const handleResendOtpCode = async () => {
		try {
			if (isModalOtpConfirmationOpen.type === AlertEnum.CELLPHONE) {
				await resendPhoneOtpCode(isModalOtpConfirmationOpen.data as string);

				return;
			}

			await resendEmailOtpCode(isModalOtpConfirmationOpen.data as string);
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(e);
		}
	};

	const handleToggleOtpModal = (open: boolean) => {
		setIsModalOtpConfirmationOpen({open});
	};

	const handleAbandonOtpModal = () => {
		setIsModalOtpConfirmationOpen({isAbandoned: true});
	};

	return {
		handleChangeDataFocus,
		handleChangeDataFormOpen,
		handleOnConfirmDiscardChangesModal,
		handleToggleOtpModal,
		isModalDiscardChangesOpenState,
		isModalOtpConfirmationOpen,
		changeDataFormState,
		setIsModalDiscardChangesOpenState,
		handleAbandonOtpModal,
		handleResendOtpCode,
		changeAddressIsLoading,
		onCancel,
		handleSendPhoneOtpCode,
		handleSendEmailOtpCode,
		resetChangeDataFormState,
		resetOtpConfirmationModal,
		handleChangeAddress,
		setChangeDataFormState,
		changingEmailState,
		changingCellphoneState,
		resetCellphoneErrorBoundary,
		resetEmailErrorBoundary,
	};
}
